/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Row,
  Col,
  Spin,
  Card,
  Input,
  Button,
  DatePicker,
} from 'antd';

import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import ModalDelete from 'components/ModalDelete';
import LadaNumero from 'components/LadaNumero';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import {
  baseURI,
  getContratosFoliosPlacas,
  getContratoFolioPlaca,
  patchContratoFolioPlaca,
  postContratoFolioPlaca,
  deleteContratoFolioPlaca,
} from 'api/recaudacion/contratoFolioPlacas';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';

const ContratoFolioPlaca = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [hasActionPermissions, setHasActionPermissions] = useState(true);
  const [formInfo] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    const _data = await getContratosFoliosPlacas();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = hasActionPermission('unidadresponsable', ActionsNames.view);
      if (mounted) {
        setHasActionPermissions(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _data = await getContratosFoliosPlacas();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const match = await getContratoFolioPlaca(key);
    if (match) {
      setVisible(true);
      form.setFieldsValue(match);
      formInfo.setFieldsValue(match);
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const _data = await (key ? patchContratoFolioPlaca(key, values)
        : postContratoFolioPlaca(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
            setTimeout(() => onClickEdit(_data));
          } else {
            setTimeout(() => onClickEdit());
          }
        } else {
          onCancel();
        }
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo]);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteContratoFolioPlaca(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };
  /*
  const reset = () => {
    setDepartamentoFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };
  */
  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 300,
    },
    {
      titleText: 'Fecha de Firma',
      dataIndex: 'fecha_firma',
      key: 'fecha_firma',
      width: 300,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Contacto',
      dataIndex: 'nombre_del_contacto',
      key: 'nombre_del_contacto',
      width: 300,
    },
    {
      titleText: 'Teléfono de Contacto',
      dataIndex: 'telefono_contacto',
      key: 'telefono_contacto',
      width: 300,
    },
    {
      titleText: 'Cantidad de Placas',
      dataIndex: 'cantidad_de_placas',
      key: 'cantidad_de_placas',
      width: 300,
    },
    {
      titleText: 'Cantidad de Tarjetas',
      dataIndex: 'cantidad_de_tarjetas',
      key: 'cantidad_de_tarjetas',
      width: 300,
    },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_de_registro',
      key: 'fecha_de_registro',
      width: 300,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => (estadosGlobales.find((item) => item.id === val).descripcion),
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
      },
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Col xs={24} sm={24} md={24}>
        <Spin
          tip="Cargando..."
          spinning={loading}
        >
          {!visible ? (
            <Table
              pagination
              cols={columns}
              data={data}
              rowKey="id"
              baseURI={baseURI}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              hasAllViewPermission={hasActionPermissions}
              permission={permission}
            />
          ) : (
            <Card
              title={(
                <FormSubmitControls
                  label={`${selectedRowKeys.length ? ' Editar' : 'Agregar'} Contrato de Folio de la Placa`}
                  onCancel={onCancel}
                  loading={loading}
                  onFinish={onFinish}
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                initialValues={{ estados_globales: 1 }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="folio"
                      label="Folio"
                      rules={rules.required}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="fecha_firma"
                      label="Fecha de la Firma"
                      hasFeedback
                      rules={rules.required}
                    >
                      <DatePicker
                        placeholder=""
                        format={DATE_FRONTEND_FORMAT}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="nombre_del_contacto"
                      label="Nombre de Contacto"
                      rules={rules.required}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col lg={8} md={16} sm={24} xs={24}>
                    <LadaNumero
                      form={form}
                      rules={rules}
                      fullSize
                      names={{
                        lada: 'lada',
                        numero_de_telefono: 'telefono_contacto',
                      }}
                      celular
                    />
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="cantidad_de_placas"
                      label="Cantidad de Placas"
                      required
                    />
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="cantidad_de_tarjetas"
                      label="Cantidad de Tarjetas"
                      required
                    />
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="fecha_de_registro"
                      label="Fecha del Registro"
                      hasFeedback
                      rules={rules.required}
                    >
                      <DatePicker
                        placeholder=""
                        format={DATE_FRONTEND_FORMAT}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      rules={rules.required}
                      label="Estado"
                      hasFeedback
                    >
                      <Select dataSource={estadosGlobales} disabled={!selectedRowKeys.length} />
                    </Form.Item>
                  </Col>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Form>
            </Card>
          )}
          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            visible={visibleAlert}
            content={`ContratoFolioPlaca ${form.getFieldValue('nombre')}`}
            loading={loading}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default ContratoFolioPlaca;
